import { Component, Prop, h, State, Watch, Host, Event, EventEmitter, Element, VNode } from '@stencil/core';
import { Table, getCoreRowModel, ColumnDef, TableState, PaginationState, getPaginationRowModel, Cell } from '@tanstack/table-core';
import { flexRender, useStencilTable } from './stencil-adapter';

@Component({
  tag: 'pnk-table',
  styleUrl: 'pnk-table.css',
})
export class PnkTable {
  @Element() host!: HTMLElement;

  @Prop() name?: string;

  @Prop({ mutable: true }) columns: ColumnDef<any>[] = [];

  @Prop({ mutable: true }) data: any[] = [];

  @State() table!: Table<any>;

  @State() tableState: TableState | object = {};

  @State() paginationState: PaginationState = {
    pageIndex: 0,
    pageSize: 5,
  };

  @Prop({ mutable: true }) pageSizeOptions: number[] = [5, 10, 20, 50];

  @Event({ eventName: 'paginationChange' })
  onPaginationChange!: EventEmitter<PaginationState>;

  @Event({ eventName: 'rowClick' })
  onRowClick!: EventEmitter<{ row: unknown; index: number }>;

  @Watch('tableState')
  watchTableStateHandler(_newTableState: TableState, _oldTableState: TableState) {
    this.createTable();
  }

  createTable() {
    this.table = useStencilTable<any>({
      state: { ...this.tableState, pagination: this.paginationState },
      data: this.data,
      columns: this.columns,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onStateChange: updater => {
        this.tableState = typeof updater === 'function' ? updater(this.table.getState()) : updater;
      },
      onPaginationChange: updater => {
        this.paginationState = typeof updater === 'function' ? updater(this.paginationState) : updater;
        this.onPaginationChange.emit(this.paginationState);
        console.log('onPaginationChange', this.paginationState);
        this.refreshTable();
      },
      manualPagination: false,
    });
  }

  getFilteredData() {
    const { pageIndex, pageSize } = this.paginationState;
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    const data = this.data.slice(start, end);
    return data;
  }

  getCustomColumnTemplateById(id: string) {
    const slotContainer = this.host.querySelector('#slotFather')?.firstElementChild;

    if (!slotContainer || slotContainer.slot !== 'columns') {
      return undefined;
    }
    const template = slotContainer.querySelector(`#${id}`);
    return template;
  }

  renderCustomColumnTemplate(cell: Cell<any, unknown>) {
    const { id } = cell.column.columnDef;
    if (id) {
      const template = this.getCustomColumnTemplateById(id);
      const tempParent = document.createElement('div');
      const templateClone = template?.cloneNode(true) as Element;
      tempParent.appendChild(templateClone);
      if (template && templateClone) {
        const valueText = '$value';
        const valuePosition = this.findValuePositionInTemplate(tempParent, valueText);
        if (valuePosition !== -1) {
          const value = flexRender(cell.column.columnDef.cell, cell.getContext());
          const valueString = value !== undefined && value !== null ? value.toString() : '';
          const templateText = tempParent.innerHTML;
          const newTemplateText = templateText.replace(/\$value/gm, valueString);
          tempParent.innerHTML = newTemplateText;
        } else {
          console.warn(`Template for column "${id}" has no "$value" placeholder`);
        }
        console.log('tempParent', tempParent.innerHTML);
        let vNode;
        if (templateClone.childElementCount > 0) {
          vNode = h(templateClone.tagName.toLowerCase(), this.generateVDataFromElement(templateClone), tempParent.textContent || tempParent.innerText);
        } else {
          vNode = this.generatevNodeTreeFromElement(tempParent.firstElementChild!);
        }
        // const vNode = this.generatevNodeTreeFromElement(tempParent.firstElementChild!);
        tempParent.remove();
        console.log('vNode', vNode);
        return vNode;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  generateVDataFromElement(el: Element): object {
    let vData = {};
    Array.from(el.attributes || []).forEach(attr => {
      vData = { ...vData, [attr.name]: attr.value };
    });
    return vData;
  }

  generatevNodeTreeFromElement(element: Node): VNode | string | null {
    const el = element as HTMLElement;
    let result: VNode | null = null;
    let vData = {};
    Array.from(el.attributes || []).forEach(attr => {
      vData = { ...vData, [attr.name]: attr.value };
    });
    if (element.hasChildNodes()) {
      let vNodeChildren = Array.from(element.childNodes).map(child => {
        const childvNode = this.generatevNodeTreeFromElement(child);
        if (childvNode) {
          return childvNode;
        }
        return null;
      });
      vNodeChildren = vNodeChildren.filter(c => c !== null);

      if (vNodeChildren.length === 1 && typeof vNodeChildren[0] === 'string') {
        result = h(el.tagName.toLowerCase(), vData, vNodeChildren[0]);
      } else {
        const filteredChildren = vNodeChildren.filter(c => typeof c !== 'string') as VNode[];
        result = h(el.tagName.toLowerCase(), vData, filteredChildren);
      }
    } else if (el.nodeName !== '#text' && el.tagName) {
      result = h(el.tagName.toLowerCase(), vData, el.innerHTML);
    } else if (el.nodeName === '#text') {
      return el.textContent || el.innerText;
    } else if (el.nodeName === '#comment') {
      return null;
    }
    return result;
  }

  findValuePositionInTemplate(template: Element, valueText = '$value') {
    const templateText = template.innerHTML;
    const valuePosition = templateText.indexOf(valueText);
    return valuePosition;
  }

  componentWillLoad() {
    this.createTable();
    setTimeout(() => {
      this.refreshTable();
    }, 50);
  }

  /**
   * Refresh the table rendering from scratch by passing the same state as a new variable reference
   * (this is possible through the Watch() decorator on "tableState").
   */
  refreshTable() {
    this.tableState = { ...this.tableState };
  }

  render() {
    return (
      <Host>
        <div class="pnk-invisible pnk-w-0 pnk-h-0" id="slotFather">
          <slot name="columns"></slot>
        </div>

        <table
          class={'pnk-flex pnk-flex-col pnk-w-full pnk-h-full pnk-bg-surface pnk-rounded-[5px] pnk-p-[5px]'}
          // {...{ style: { width: `${this.table.getCenterTotalSize().toString()}px` } }}
        >
          <div class="pnk-w-full pnk-flex pnk-flex-row pnk-justify-between pnk-items-center">
            {this.name ? <p class="pnk-px-2.5 pnk-py-[5px] pnk-text-disabled-text pnk-font-bold">{this.name}</p> : null}
            <slot name="toolbar"></slot>
          </div>
          <thead class="pnk-w-full pnk-bg-disabled-base pnk-text-disabled-text pnk-rounded-[5px]">
            {this.table.getHeaderGroups().map(headerGroup => (
              <tr class="pnk-flex" key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    class={'pnk-w-full pnk-text-disabled-text pnk-font-bold pnk-text-sm pnk-px-5 pnk-py-2.5 pnk-text-left'}
                    {...{
                      key: header.id,
                      colSpan: header.colSpan,
                      // style: {
                      //   width: `${header.getSize().toString()}px`,
                      // },
                    }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody class="pnk-py-[5px] pnk-flex pnk-flex-col pnk-gap-[5px]">
            {this.table.getPaginationRowModel().rows.map(row => (
              <tr key={row.id} class="pnk-flex pnk-w-full pnk-bg-white pnk-rounded-[5px] pnk-text-black pnk-py-2.5 hover:pnk-bg-secondary-base hover:pnk-cursor-pointer" onClick={() => this.onRowClick.emit({row: row.original, index: row.index})}>
                {row.getVisibleCells().map(cell => (
                  <td
                    id={`c${cell.row.id}_${cell.id}`}
                    key={cell.id}
                    class="pnk-px-5 pnk-w-full"
                    // {...{
                    //   style: {
                    //     width: `${cell.column.getSize().toString()}px`,
                    //   },
                    // }}
                  >
                    {cell.column.columnDef.id && this.getCustomColumnTemplateById(cell.column.columnDef.id)
                      ? this.renderCustomColumnTemplate(cell)
                      : flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <div class="pnk-flex pnk-flex-row pnk-justify-between pnk-items-center pnk-px-[15px] pnk-py-2.5">
            <div class="pnk-flex pnk-gap-1 pnk-items-center">
              <p>Righe da mostrare</p>
              <pnk-select
                loading={false}
                value={this.table.getState().pagination.pageSize.toString() || this.paginationState.pageSize.toString()}
                optionsData={this.pageSizeOptions.map(o => ({ value: `${o}`, text: `${o}` }))}
                onPnkChange={opt => this.table.setPageSize(Number.parseInt(opt.detail.value, 10))}
                class="pnk-w-min"
              />
            </div>
            <div class="pnk-flex pnk-gap-2 pnk-items-center pnk-text-disabled-text">
              <pnk-icon icon="pnk-arrow-first" class="pnk-cursor-pointer" onClick={() => this.table.setPageIndex(0)} />
              <pnk-icon icon="pnk-left h-[20px] text-[15px]" class="pnk-cursor-pointer" onClick={() => (this.table.getCanPreviousPage() ? this.table.previousPage() : null)} />
              <p class="pnk-text-sm">
                Pagina {this.table.getState().pagination.pageIndex + 1} di {this.table.getPageCount()}
              </p>
              <pnk-icon icon="pnk-right h-[20px] text-[15px]" class="pnk-cursor-pointer" onClick={() => (this.table.getCanNextPage() ? this.table.nextPage() : null)} />
              <pnk-icon icon="pnk-arrow-last" class="pnk-cursor-pointer" onClick={() => this.table.setPageIndex(this.table.getPageCount() - 1)} />
            </div>
          </div>
        </table>
      </Host>
    );
  }
}
